import type { NavigationGuard } from 'vue-router';
import { useAuthStore } from '@/modules/core/stores/auth.store.ts';
import { usePreviewStore } from '@/modules/core/stores/preview.store.ts';
import { Route } from '@/modules/core/enums/route.enum.ts';
import { ErrorCode } from '@/modules/common/enums/error-code.ts';
import { useAccessStore } from '@/modules/core/stores/access.store.ts';
import { useUserStore } from '@/modules/core/stores/user.store.ts';
import { useRolesStore } from '@/modules/core/stores/roles.store.ts';

export function createNavigationGuard(): NavigationGuard {
  let isInitialNavigation = true;

  return async (to, _, next) => {
    const previewStore = usePreviewStore();

    previewStore.setEntityIdPreviewAction(null);

    const authStore = useAuthStore();

    const isFreshStart = isInitialNavigation;
    isInitialNavigation = false;

    if (isFreshStart && authStore.isAuthorized) {
      try {
        await authStore.refresh();
      } catch {
        next({ name: Route.LoginPage, query: { redirect: to.fullPath } });
        return;
      }
    }

    if (authStore.isAuthorized) {
      if (to.name === Route.LoginPage) {
        next({ name: Route.AssignmentsPage });
        return;
      }

      const accessStore = useAccessStore();

      const stores = [useUserStore(), useRolesStore(), accessStore];
      const storesToFetch = isFreshStart ? stores : stores.filter((store) => !store.isDefined);

      try {
        await Promise.all(storesToFetch.map((store) => store.fetch()));
      } catch {
        next({
          name: Route.ErrorPage,
          state: {
            code: ErrorCode.InternalServerError,
          },
        });
        return;
      }

      if (to.meta.access && !accessStore.has(to.meta.access)) {
        next({
          name: Route.ErrorPage,
          state: {
            code: ErrorCode.Forbidden,
          },
        });
        return;
      }
    } else {
      if (to.meta.auth !== false) {
        next({ name: Route.LoginPage, query: { redirect: to.fullPath } });
        return;
      }
    }

    next();
  };
}
